"use client";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { formatDate } from "@/lib";
import { IdType, OrderType } from "@/types";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useDateSelect } from "./useDateSelect";

export type DateSelectProps = {
  userId: IdType;
  dates?: OrderType[];
  orderId?: OrderType["orderId"];
  slug: string;
};

export const DateSelect = ({ dates, orderId, userId, slug }: DateSelectProps) => {
  const safeDates = Array.isArray(dates) ? dates : [];
  const { isPending, currentIndex, handleArrowLeft, handleArrowRight, handleSelectChange } =
    useDateSelect({
      dates: safeDates,
      orderId,
      userId,
      slug,
    });

  const lastIndex = Math.max(0, safeDates.length - 1);
  const isEmpty = safeDates.length === 0;

  return (
    <Select
      value={orderId ?? safeDates[0]?.orderId ?? ""}
      onValueChange={value => handleSelectChange(value)}
      disabled={isPending || isEmpty}
    >
      <ButtonGroup>
        <Button
          size="icon-sm"
          variant="outline"
          disabled={isPending || isEmpty || currentIndex <= 0}
          onClick={handleArrowLeft}
        >
          <ChevronLeftIcon className="text-foreground" />
        </Button>

        <SelectTrigger size="sm" className="[&>svg:last-child]:hidden font-medium min-w-36">
          <SelectValue />
          {isPending ? <Spinner /> : <ChevronDownIcon className="text-foreground" />}
        </SelectTrigger>

        <Button
          size="icon-sm"
          variant="outline"
          disabled={isPending || isEmpty || currentIndex >= lastIndex}
          onClick={handleArrowRight}
        >
          <ChevronRightIcon className="text-foreground" />
        </Button>
      </ButtonGroup>

      <SelectContent>
        {safeDates.map(d => (
          <SelectItem key={d.orderId} value={d.orderId}>
            {formatDate(d.deliveryDate)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
